export const EASY_ONBOARD_API_KEY_PLACEHOLDER =
  "nos_sk_*******************************************";
export const EASY_ONBOARD_AWS_ACCESS_KEY_PLACEHOLDER = "********************";
export const EASY_ONBOARD_AWS_SECRET_KEY_PLACEHOLDER =
  "****************************************";

const DAY_IN_MILLISECONDS = 24 * 60 * 60 * 1000;

export type EasyOnboardProduct = "world" | "atlas" | "world-atlas" | "world-tickers";
export const TRIAL_VERSION = "V2.0";
export function trialProducts(product: EasyOnboardProduct): Array<"world" | "atlas"> {
  return product === "world-atlas" ? ["world", "atlas"] : [product === "world-tickers" ? "world" : product];
}
export function trialProductLabel(product: EasyOnboardProduct): string {
  if (product === "world-tickers") return `World ${TRIAL_VERSION} (ticker-only)`;
  return trialProducts(product).map((value) => `${value === "world" ? "World" : "Atlas"} ${TRIAL_VERSION}`).join(" + ");
}
export function validTrialAccess(access: EasyOnboardAccess): boolean {
  return !(access.staticS3 && access.liveS3) && (access.webApi || access.staticS3 || access.liveS3);
}
export function validProductTrialAccess(product: EasyOnboardProduct, access: EasyOnboardAccess): boolean {
  return validTrialAccess(access) && (product !== "world-tickers" || (!access.webApi && !access.staticS3 && access.liveS3));
}

export type EasyOnboardRecipient = {
  name: string;
  email: string;
};

export type EasyOnboardAccess = {
  webApi: boolean;
  staticS3: boolean;
  liveS3: boolean;
};

export type EasyOnboardBucket = {
  kind: "static" | "live";
  name: string;
  region: string;
  product?: "world" | "atlas";
  prefix?: string;
};

export type EasyOnboardEmailContentInput = {
  product?: EasyOnboardProduct;
  recipients: EasyOnboardRecipient[];
  organization: string;
  access: EasyOnboardAccess;
  apiKey: string | null;
  awsAccessKeyId: string | null;
  awsSecretAccessKey: string | null;
  buckets: EasyOnboardBucket[];
  startsAt: Date;
  expiresAt: Date;
};

function formatTrialDate(value: Date): string {
  return new Intl.DateTimeFormat("en-GB", {
    dateStyle: "long",
    timeZone: "UTC",
  }).format(value);
}

function joinNames(names: string[]): string {
  if (names.length === 1) return names[0]!;
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(", ")}, and ${names.at(-1)}`;
}

function trialLengthInDays(startsAt: Date, expiresAt: Date): number {
  return Math.max(
    1,
    Math.round((expiresAt.getTime() - startsAt.getTime()) / DAY_IN_MILLISECONDS),
  );
}

function loginSummary(recipients: EasyOnboardRecipient[]): string {
  if (recipients.length === 1) {
    return `* You can log into app.nosible.com using ${recipients[0]!.email}.`;
  }

  return `* You can each log into app.nosible.com using your own email address:
${recipients
  .map((recipient) => `  - ${recipient.name}: ${recipient.email}`)
  .join("\n")}`;
}

function datasetText(
  heading:
    | "Trial Dataset (Historic Only)"
    | "Trial Dataset (Historic + Current)",
  bucket: EasyOnboardBucket,
  awsAccessKeyId: string,
  awsSecretAccessKey: string,
  tickerOnly = false,
): string {
  const endpoint = `https://${bucket.name}.s3.${bucket.region}.amazonaws.com`;
  const product = bucket.product ?? "world";
  const prefix = bucket.prefix ?? "slices_v2_0/";
  const contents = product === "world"
    ? `* Date Folders: ${prefix}yyyy-mm-dd/
* Date Contents:
  - events.ndjson - newline-delimited events in JSON format.
  - events.locs - byte offsets for each event in events.ndjson.
  - events_oai.hnsw - the HNSW graph for querying that day's events.
  - events_map.ipc - Arrow metadata for filtering that day's events.
  - graph/ - the day's entity relationship graph:
    - nodes.ndjson - normalized entities in the graph.
    - edges.ndjson - relationships connecting entities.
    - edge_occurrences.ndjson - relationship occurrences linked to supporting events.
    - events.ndjson - event identifiers and source record locations.
    - graph.sqlite - SQLite database for querying graph data.`
    : `* Date Folders: ${prefix}yyyy-mm-dd/
* Graph Contents (inside each date's graph/ folder):
  - nodes.ndjson - normalized graph entities.
  - edges.ndjson - relationships between entities.
  - edge_occurrences.ndjson - dated supporting evidence for relationships.
  - events.ndjson - graph event evidence.
  - graph.sqlite - the graph in SQLite format.`;
  return `**${trialProductLabel(tickerOnly ? "world-tickers" : product)} Trial Dataset**

* Delivery: ${heading.replace("Trial Dataset (", "").replace(")", "")}

* S3 Access Key: ${awsAccessKeyId}
* S3 Secret Key: ${awsSecretAccessKey}
* S3 Bucket: ${endpoint}
* Data Prefix: ${prefix}
${contents}`;
}

/**
 * Builds the shared subject and editable plain-text body used by both the
 * browser's live preview and the server's final credential-bearing email.
 */
export function buildEasyOnboardEmailContent(
  input: EasyOnboardEmailContentInput,
): {subject: string; body: string} {
  const firstNames = input.recipients.map(
    (recipient) =>
      recipient.name.trim().split(/\s+/)[0] || recipient.name.trim(),
  );
  const greeting = joinNames(firstNames);
  const startDate = formatTrialDate(input.startsAt);
  const expiryDate = formatTrialDate(input.expiresAt);
  const trialLength = trialLengthInDays(input.startsAt, input.expiresAt);
  const product = input.product ?? "world";
  const textSections: string[] = [`Hi ${greeting},

Your ${trialLength}-day trial has started \u{1F389}!

**Bottom Line Upfront**

* Your trial starts on ${startDate} and ends on ${expiryDate}.
* Your trial includes ${trialProductLabel(product)}.
${loginSummary(input.recipients)}
* On app.nosible.com you can add users${product === "world-tickers" ? "." : " and new API keys."}
* Detailed API documentation is available at docs.nosible.com.

**Trial Resources**

* Published Research: https://nosible.com/blog
* API Reference: https://docs.nosible.com/
* Data Dictionaries: https://nosible.com/data-dictionaries
* Ontology Reference: https://nosible.com/ontologies
* Atlas Relationship Catalog: https://nosible.com/relationships
* Exploratory Data Analysis: https://nosible.com/start-trial
* Legal & Compliance: https://nosible.com/legal/compliance

**Trial Permissions**

${input.access.webApi ? "[x]" : "[ ]"} Managed Web API
${input.access.staticS3 ? "[x]" : "[ ]"} S3 Dataset (Historic Only)
${input.access.liveS3 ? "[x]" : "[ ]"} S3 Dataset (Historic + Current)`];

  if (input.access.webApi && input.apiKey) {
    textSections.push(`**NOSIBLE Web API**

* API Reference: https://docs.nosible.com/
* API Base URL: https://nosible.world/api/v2.0/${product === "atlas" ? "atlas/" : ""}
${product === "world-atlas" ? "* Atlas API Base URL: https://nosible.world/api/v2.0/atlas/\n" : ""}* API Key: ${input.apiKey}`);
  }

  for (const bucket of input.buckets) {
    if (!trialProducts(product).includes(bucket.product ?? "world")) continue;
    if (!(bucket.kind === "static" ? input.access.staticS3 : input.access.liveS3)) continue;
    if (!input.awsAccessKeyId || !input.awsSecretAccessKey) continue;
    textSections.push(datasetText(
      bucket.kind === "static" ? "Trial Dataset (Historic Only)" : "Trial Dataset (Historic + Current)",
      bucket, input.awsAccessKeyId, input.awsSecretAccessKey, product === "world-tickers",
    ));
  }

  textSections.push(`**Recommended Next Steps**

We've published a lot of research that demonstrates how to use NOSIBLE to develop novel signals. Go take a look: https://nosible.com/blog.

Kind regards,
Stuart Reid`);

  if (input.awsAccessKeyId || input.apiKey) {
    textSections.push(
      "Please store these credentials in a password manager or secrets vault and do not commit them to source control.",
    );
  }

  return {
    subject: ({
      world: "NOSIBLE World V2.0 Trial Activated - All events - Credentials and Getting Started",
      "world-tickers": "NOSIBLE World V2.0 Trial Activated - Ticker-only - Credentials and Getting Started",
      atlas: "NOSIBLE Atlas V2.0 Trial Activated - Credentials and Getting Started",
      "world-atlas": "NOSIBLE World V2.0 + Atlas V2.0 Trial Activated - All events - Credentials and Getting Started",
    } satisfies Record<EasyOnboardProduct, string>)[product],
    body: textSections.join("\n\n"),
  };
}
